'use client';

import { useMemoizedFn, useSetState } from 'ahooks';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { toast } from '@/app/components/ui/use-toast';
import { useDevice, useFetch, useGlobalModal, useRunTempUploadBatch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  CreationInfo,
  CreationWithTask,
  ToolTaskFormVideoPrompt,
  VideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import { isExtendTask, mergeCreationAndTask, normalizeDiscreteVideoDurationToSeconds } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { UpscaleResolutionSelector } from '../_components/upscale/UpscaleResolutionSelector';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolCreationRecreatePatch,
  createPromptListHelpers,
  createToolVideoPromptFromUploadFile,
  getDefaultUpscaleTargetResolution,
  getUpscaleSourceResolution,
  isResolutionBelow4k,
  isUpscaleTargetResolutionAvailable,
  UPSCALE_SOURCE_RESOLUTION_ALIASES,
  type UpscaleSourceResolution,
} from '../_utils';
import { getToolTempVideosPendingItems } from '../_utils/tool-temp-upload-items';

const maxDuration = 300;
const minDuration = 5;
const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024;

interface UpscaleState {
  selectedCreation?: CreationWithTask;
}

const UpscaleForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const pathname = usePathname();
  const router = useRouter();
  const storeApi = useStoreApi();
  const searchParams = useSearchParams();
  const creationId = searchParams.get('creationId') || '';

  const { settings, updateSettings } = useToolForm('upscale');

  const [openMediaSelector, setOpenMediaSelector] = useState(false);
  const [selectedCreationIndex, setSelectedCreationIndex] = useState<number | null>(null);
  const [upscaleState, setUpscaleState] = useSetState<UpscaleState>({
    selectedCreation: undefined,
  });
  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateUpscaleVideoFormParams, videoPrompts, userInfo } = useStore((state) => ({
    updateUpscaleVideoFormParams: state.updateUpscaleVideoFormParams,
    videoPrompts: state.upscaleVideoFormParams.videoPrompts,
    userInfo: state.userInfo,
  }));

  const videoPrompt = videoPrompts?.[0] || null;

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp videos',
    (refs) =>
      getToolTempVideosPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().upscaleVideoFormParams.videoPrompts,
          (prompts) =>
            updateUpscaleVideoFormParams('videoPrompts', prompts.filter(Boolean) as ToolTaskFormVideoPrompt[]),
        ),
        { videoPrompts },
      ),
    [updateUpscaleVideoFormParams, userInfo, videoPrompts],
  );

  const showInvalidResolutionToast = useMemoizedFn(() => {
    toast({
      type: 'error',
      message: t('sharedComponent.videoUploader.error.invalidResolution4k'),
    });
  });

  const getCreationSourceResolution = useCallback(
    (creation: CreationInfo | CreationWithTask): UpscaleSourceResolution | null => {
      return getUpscaleSourceResolution(
        {
          resolution: creation.resolution?.resolution,
          width: creation.resolution?.width,
          height: creation.resolution?.height,
        },
        UPSCALE_SOURCE_RESOLUTION_ALIASES,
      );
    },
    [],
  );

  const isCreationResolutionAtLeast4k = useCallback((creation: CreationInfo | CreationWithTask): boolean => {
    return !isResolutionBelow4k({
      width: creation.resolution?.width,
      height: creation.resolution?.height,
    });
  }, []);

  const syncResolutionSelection = useCallback(
    (sourceResolution?: UpscaleSourceResolution | null, currentSelectedResolution?: string) => {
      const nextResolution = isUpscaleTargetResolutionAvailable(currentSelectedResolution, sourceResolution)
        ? currentSelectedResolution
        : getDefaultUpscaleTargetResolution(sourceResolution);

      if (settings.resolution === nextResolution) {
        return;
      }

      updateSettings({
        resolution: nextResolution,
      });
    },
    [settings.resolution, updateSettings],
  );

  const updateVideoDuration = useCallback(
    (duration?: number) => {
      if (typeof duration !== 'number') {
        return;
      }

      updateSettings({
        duration: normalizeDiscreteVideoDurationToSeconds(duration),
      });
    },
    [updateSettings],
  );

  const getCreationDuration = useCallback((creation: CreationInfo | CreationWithTask): number | undefined => {
    return (
      ('task' in creation
        ? isExtendTask(creation?.task.type)
          ? creation?.duration
          : creation?.task.settings.duration
        : creation.duration) || 0
    );
  }, []);

  const syncCreationSettings = useCallback(
    (creation: CreationInfo | CreationWithTask) => {
      syncResolutionSelection(getCreationSourceResolution(creation), settings.resolution);
      updateVideoDuration(getCreationDuration(creation));
    },
    [
      getCreationDuration,
      getCreationSourceResolution,
      settings.resolution,
      syncResolutionSelection,
      updateVideoDuration,
    ],
  );

  const applyCreationSelection = useCallback(
    (creation: CreationInfo | CreationWithTask) => {
      if (isCreationResolutionAtLeast4k(creation)) {
        showInvalidResolutionToast();
        return;
      }

      applyToolFormParamsPatch(
        updateUpscaleVideoFormParams,
        buildToolCreationRecreatePatch({ tool: 'upscale', creation }),
      );
      syncCreationSettings(creation);
    },
    [isCreationResolutionAtLeast4k, showInvalidResolutionToast, syncCreationSettings, updateUpscaleVideoFormParams],
  );

  // 从 videoPrompt 中提取视频信息
  const currentResolution = getUpscaleSourceResolution(
    {
      resolution: (videoPrompt?.prompt as VideoPrompt & { resolution?: { resolution?: string } })?.resolution
        ?.resolution,
      width: (videoPrompt?.prompt as VideoPrompt & { resolution?: { width?: number } })?.resolution?.width,
      height: (videoPrompt?.prompt as VideoPrompt & { resolution?: { height?: number } })?.resolution?.height,
    },
    UPSCALE_SOURCE_RESOLUTION_ALIASES,
  );

  const selectedResolution =
    (settings as { resolution?: string }).resolution || getDefaultUpscaleTargetResolution(currentResolution);

  useFetch(
    () =>
      getCreationDetail({
        path: {
          id: creationId,
        },
      }),
    {
      ready: !!creationId && videoPrompt?.id !== creationId,
      refreshDeps: [creationId, videoPrompt?.id],
      onSuccess: ({ data, error }) => {
        if (error || !data?.creation) return;

        const mergedCreation = mergeCreationAndTask({
          creation: data.creation,
          task: data.task,
          creationId: data.creation.id,
          extendSourceCreations: data.extend_source_creations,
        });

        if (mergedCreation.type !== 'video') {
          return;
        }

        applyCreationSelection(mergedCreation);
      },
    },
  );

  useEffect(() => {
    if (isUpscaleTargetResolutionAvailable(settings.resolution, currentResolution)) {
      return;
    }

    syncResolutionSelection(currentResolution, settings.resolution);
  }, [currentResolution, settings.resolution, syncResolutionSelection]);

  useEffect(() => {
    if (!creationId || videoPrompt?.id !== creationId) {
      return;
    }

    router.replace(pathname);
  }, [creationId, pathname, router, videoPrompt?.id]);

  const handleSelectMedia = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedCreationIndex(null);
        setUpscaleState({
          selectedCreation: undefined,
        });
        return;
      }
      setSelectedCreationIndex(index);
      setUpscaleState({
        selectedCreation: creation,
      });
    },
    [setUpscaleState],
  );

  const handleRemoveMedia = useCallback(() => {
    updateUpscaleVideoFormParams('videoPrompts', []);
    updateUpscaleVideoFormParams('creationId', undefined);
  }, [updateUpscaleVideoFormParams]);

  const validateVideoMetadata = useCallback(
    ({ width, height }: { duration: number; aspectRatio: number; width: number; height: number }) => {
      if (!isResolutionBelow4k({ width, height })) {
        return t('sharedComponent.videoUploader.error.invalidResolution4k');
      }

      return null;
    },
    [t],
  );

  const handleMediaUpload = useMemoizedFn((file: VideoUploadFile) => {
    const sourceResolution = getUpscaleSourceResolution(
      {
        width: file.width,
        height: file.height,
      },
      UPSCALE_SOURCE_RESOLUTION_ALIASES,
    );
    const videoPrompt = createToolVideoPromptFromUploadFile(
      file,
      file.file
        ? ({
            video: file.file.src,
            videoPreview: file.file.preview,
            duration: normalizeDiscreteVideoDurationToSeconds(file.file.duration),
            coverImage: file.file.cover_url,
            coverImagePreview: file.file.cover_preview_url || file.file.preview,
            width: file.width ?? file.file.width,
            height: file.height ?? file.file.height,
            aspectRatio: file.file.aspectRatio,
            videoId: file.file.id,
            _tempFile: file.file._tempFile,
            _tempCoverImage: file.file._tempCoverImage,
            resolution: {
              width: file.width || 0,
              height: file.height || 0,
              resolution: sourceResolution || '',
            },
          } as VideoPrompt & {
            resolution: {
              width: number;
              height: number;
              resolution: string;
            };
          })
        : null,
    );
    updateUpscaleVideoFormParams('videoPrompts', [videoPrompt]);

    if (file.status === 'done' && file.file) {
      syncResolutionSelection(sourceResolution, settings.resolution);
      updateVideoDuration(file.file.duration);
    }

    if (file.status === 'error' || file.status === 'removed') {
      updateUpscaleVideoFormParams('videoPrompts', []);
    }
  });

  const hasUploadingMedia = videoPrompts.some((prompt) => prompt.status === 'uploading');
  const remainingVideoSlots = videoPrompt ? 0 : 1;

  return (
    <>
      <ToolFormContainer
        title={t('tools.upscale.video.title')}
        description={t('tools.upscale.video.description')}
        exampleSection={<ExampleMediaPreview tool="upscale" />}
        mediaSection={
          <MediaDropZone
            acceptVideo={true}
            acceptImage={false}
            maxVideoCount={remainingVideoSlots}
            disabled={remainingVideoSlots === 0}
            maxVideoDuration={maxDuration}
            minVideoDuration={minDuration}
            maxVideoSize={MAX_VIDEO_FILE_SIZE}
            allowTempMode={true}
            onChange={(file, type) => {
              if (type === 'video') handleMediaUpload(file as VideoUploadFile);
            }}
          >
            <ToolMediaUpload
              type="video"
              header={
                <ToolMediaUploadHeader
                  icon={<AgentLineGenerateVideo className="size-5 text-system-white" />}
                  title={t('tools.targetVideo')}
                  rightSlot={
                    currentResolution ? (
                      <div className="flex items-center gap-1 text-sm text-system-white leading-[1.4]">
                        <span className="text-system-white/40">{t('tools.upscale.video.current')}: </span>
                        <span className="font-medium">{currentResolution}</span>
                      </div>
                    ) : null
                  }
                />
              }
              emptyActionsLayout="inline"
              videoPrompt={videoPrompt}
              isMobile={isMobile}
              maxDuration={maxDuration}
              minDuration={minDuration}
              maxVideoSize={MAX_VIDEO_FILE_SIZE}
              validateVideoMetadata={validateVideoMetadata}
              onVideoUpload={handleMediaUpload}
              onOpenSelector={() => setOpenMediaSelector(true)}
              onRemoveMedia={handleRemoveMedia}
            />
          </MediaDropZone>
        }
        settingsSection={
          <UpscaleResolutionSelector
            selectedResolution={selectedResolution}
            onResolutionChange={(resolution) => {
              updateSettings({ resolution });
            }}
            currentResolution={currentResolution || undefined}
          />
        }
        submitButton={
          <ToolFormSubmitButton
            taskType="upscale"
            disabled={hasUploadingMedia || !videoPrompt || videoPrompt.status !== 'done'}
            disabledTip={hasUploadingMedia ? t('create.createForm.error.uploadingMedia') : undefined}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openMediaSelector}
        onOpenChange={setOpenMediaSelector}
        selectedIndex={selectedCreationIndex}
        mediaType="video"
        onSelectionChange={handleSelectMedia}
        isCreationDisabled={isCreationResolutionAtLeast4k}
        onConfirm={() => {
          setOpenMediaSelector(false);
          const selectedCreation = upscaleState.selectedCreation;
          if (selectedCreation) {
            applyCreationSelection(selectedCreation);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default UpscaleForm;
